// No imports on purpose: the API server loads this file at runtime
// (routes/v2/questionnaires.ts) and cannot resolve the UI's `src/` alias.

/**
 * Container-hardening rollout (non-root migration), set per cluster
 * (noerkelit/aw_anketa#248). Rendered as `hardening.<workload>.<step>` in the
 * opsanio values; opsanio-helm-values >= 0.2.219 reads `hardening.codex.fixPerms`.
 *
 * The ONE list of components per workload. Rollout order: more codex components
 * follow (agents, webhook, ipfix, eventing, nuclei, snmp, rimor), then further
 * workloads (wazuh, openvas, semaphrone, most) as their own keys here.
 */
export const OPSANIO_HARDENING_COMPONENTS = {
  codex: ["merger", "internal", "trivy"],
} as const;

export type OpsanioHardeningWorkload = keyof typeof OPSANIO_HARDENING_COMPONENTS;

/**
 * Rollout steps, as question id (questionnaire fact) -> values key. A step is
 * a list of components per workload. `run_as_non_root` -> `runAsNonRoot` joins
 * here when the user switch itself is rolled out.
 */
export const OPSANIO_HARDENING_STEPS = {
  fix_perms: "fixPerms",
} as const;

export type OpsanioHardeningStepKey =
  (typeof OPSANIO_HARDENING_STEPS)[keyof typeof OPSANIO_HARDENING_STEPS];

export type OpsanioHardening = {
  [W in OpsanioHardeningWorkload]?: {
    [S in OpsanioHardeningStepKey]?: string[];
  };
};

/** Questionnaire facts of this section start with this prefix. */
export const HARDENING_FACT_PREFIX = "hardening.";

/**
 * A multi-select answer as a clean component list: accepts an array or a
 * comma-separated string, drops anything not in `allowed`, de-duplicates and
 * returns it in the canonical order of `allowed` - so the same selection
 * always renders the same bytes.
 */
export const toHardeningList = (
  value: unknown,
  allowed: readonly string[],
): string[] => {
  const raw = Array.isArray(value)
    ? value.map((v) => String(v))
    : typeof value === "string"
      ? value.split(",")
      : [];
  const picked = new Set(raw.map((v) => v.trim()));
  return allowed.filter((component) => picked.has(component));
};

/**
 * The hardening block for the values file, from questionnaire facts keyed
 * `hardening.<workload>.<step>` (each fact `{ value }` or a bare value).
 * Returns undefined when nothing is selected, so the key is left out entirely
 * and an unconfigured cluster renders byte-identical values.
 */
export const hardeningFromFacts = (
  facts: Record<string, unknown> | null | undefined,
): OpsanioHardening | undefined => {
  if (!facts || typeof facts !== "object") return undefined;
  const result: Record<string, Record<string, string[]>> = {};

  for (const [workload, allowed] of Object.entries(OPSANIO_HARDENING_COMPONENTS)) {
    for (const [stepId, stepKey] of Object.entries(OPSANIO_HARDENING_STEPS)) {
      const fact = facts[`${HARDENING_FACT_PREFIX}${workload}.${stepId}`];
      const value =
        fact !== null && typeof fact === "object" && !Array.isArray(fact)
          ? (fact as { value?: unknown }).value
          : fact;
      const list = toHardeningList(value, allowed);
      if (list.length > 0) {
        result[workload] = { ...(result[workload] ?? {}), [stepKey]: list };
      }
    }
  }
  return Object.keys(result).length > 0 ? (result as OpsanioHardening) : undefined;
};

/**
 * Read filter for the questionnaire GET: the Hardening section is internal-only,
 * so callers without `opsanio/hardening · view` get the content without any
 * `hardening.*` fact. Accepts the stored content as a JSON string or an object
 * and returns the same kind; anything unparsable is returned unchanged.
 */
export const stripHardeningFacts = <T>(content: T): T => {
  const facts =
    typeof content === "string"
      ? (() => {
          try {
            return JSON.parse(content);
          } catch {
            return null;
          }
        })()
      : content;
  if (!facts || typeof facts !== "object" || Array.isArray(facts)) return content;
  const keys = Object.keys(facts);
  if (!keys.some((key) => key.startsWith(HARDENING_FACT_PREFIX))) return content;

  const kept: Record<string, unknown> = {};
  for (const key of keys) {
    if (!key.startsWith(HARDENING_FACT_PREFIX)) {
      kept[key] = (facts as Record<string, unknown>)[key];
    }
  }
  return (typeof content === "string" ? JSON.stringify(kept) : kept) as T;
};

/**
 * Write guard for the questionnaire PUT. `stored` and `incoming` are the facts
 * objects. Only keys PRESENT in `incoming` count as a change attempt: a client
 * whose catalog has no hardening section (an older cached UI) simply omits
 * them, and those are carried over from `stored` instead of being wiped.
 *
 * Returns `changed` (some present key differs from what is stored) and
 * `merged` (incoming plus any stored hardening fact it left out).
 */
export const reconcileHardeningFacts = (
  stored: Record<string, unknown> | null | undefined,
  incoming: Record<string, unknown>,
): { changed: boolean; merged: Record<string, unknown> } => {
  const before = hardeningFromFacts(stored);
  const merged: Record<string, unknown> = { ...incoming };
  for (const [key, fact] of Object.entries(stored ?? {})) {
    if (key.startsWith(HARDENING_FACT_PREFIX) && !(key in merged)) {
      merged[key] = fact;
    }
  }
  const after = hardeningFromFacts(merged);
  return {
    changed: JSON.stringify(before ?? null) !== JSON.stringify(after ?? null),
    merged,
  };
};
